import React, { useState } from 'react';
import PropTypes from 'prop-types';
import './ReReviewSummary.scss';

var moment = require('moment');

const TRIGGER_LABELS = {
    pendingRejected: 'Pending rejected',
    publishedUpdated: 'Published + updated',
    unpublishedUpdated: 'Unpublished + updated',
};

// Long values (the JD) are cut in the table; the full text is one click away
const PREVIEW_LENGTH = 160;

const formatTime = (value) => value ? moment(value).format('DD MMM, hh:mm A') : '';

const stripHtml = (value) => String(value === null || value === undefined ? '' : value).replace(/<[^>]*>/g, ' ').replace(/&nbsp;/g, ' ').replace(/\s+/g, ' ').trim();

const ChangeValue = ({ value }) => {
    const [expanded, setExpanded] = useState(false);
    const text = stripHtml(value);
    if (!text) return <span className='rr-empty'>(empty)</span>;
    if (text.length <= PREVIEW_LENGTH || expanded) return <span>{text}</span>;
    return (
        <span>
            {text.slice(0, PREVIEW_LENGTH)}…{' '}
            <button type='button' className='rr-link' onClick={() => setExpanded(true)}>more</button>
        </span>
    );
}

// Re-review of a job the recruiter edited after review (recruiter-api Containers/Job/reReview):
// what the edited version trips now against what it was rejected / flagged for, and for a
// published job which fields changed against the live version. Flag-only - nothing here acts on the job.
const ReReviewSummary = ({ item }) => {
    const [showChanges, setShowChanges] = useState(false);
    const reReview = item.reReview;
    const salaryNote = item.salaryDisclosedOnPublish == 1;

    if (!reReview && !salaryNote) return null;

    const trigger = reReview ? (TRIGGER_LABELS[reReview.trigger] || reReview.trigger) : '';
    const labelOf = (code) => {
        const found = [...(reReview.reasons || []), ...(reReview.previousReasons || [])].find(each => each.code === code);
        return found ? found.label : code;
    }
    const detailOf = (code) => {
        const found = (reReview.reasons || []).find(each => each.code === code);
        return found ? found.detail : null;
    }
    const changes = Array.isArray(reReview && reReview.changes) ? reReview.changes : null;

    const chip = (code, kind) => (
        <span className={`rr-chip rr-chip-${kind}`} key={kind + code}>
            {labelOf(code)}
            {detailOf(code) ? <span className='rr-why' tabIndex={0}> ?<span className='rr-why-tip' role='tooltip'>{detailOf(code)}</span></span> : null}
        </span>
    );

    return (
        <div className='rereview-summary'>
            {
                reReview ?
                <div>
                    <div className='rr-heading'>
                        AI re-review · {trigger}
                        {reReview.editedAt ? <span className='rr-muted'> · edited {formatTime(reReview.editedAt)}</span> : null}
                    </div>
                    {
                        reReview.status === 'queued' ?
                        <div className='rr-muted'>
                            AI suggestions will be re-run {moment(reReview.dueAt).isAfter(moment()) ? `on ${formatTime(reReview.dueAt)}` : 'shortly'}
                            {(reReview.previousReasons || []).length ? <span> · was: {reReview.previousReasons.map(each => each.label).join(', ')}</span> : null}
                        </div>
                        : null
                    }
                    {
                        reReview.status === 'done' ?
                        <div>
                            {
                                (reReview.resolved || []).length || (reReview.stillPresent || []).length || (reReview.newReasons || []).length ?
                                <div className='rr-reasons'>
                                    {(reReview.stillPresent || []).length ? <div><span className='rr-label'>Still present:</span> {reReview.stillPresent.map(code => chip(code, 'still'))}</div> : null}
                                    {(reReview.newReasons || []).length ? <div><span className='rr-label'>New:</span> {reReview.newReasons.map(code => chip(code, 'new'))}</div> : null}
                                    {(reReview.resolved || []).length ? <div><span className='rr-label'>Fixed:</span> {reReview.resolved.map(code => chip(code, 'fixed'))}</div> : null}
                                </div>
                                : <div className='rr-clean'>Updated version is clean - no rejection reason found</div>
                            }
                            {
                                changes ?
                                <div>
                                    <button type='button' className='rr-link' onClick={() => setShowChanges(!showChanges)}>
                                        {changes.length ? `${showChanges ? 'Hide' : 'Show'} ${changes.length} change${changes.length > 1 ? 's' : ''} vs published version` : 'No field changed vs published version'}
                                    </button>
                                    {
                                        showChanges && changes.length ?
                                        <table className='rr-changes'>
                                            <thead><tr><th>Field</th><th>Published</th><th>Updated</th></tr></thead>
                                            <tbody>
                                                {changes.map(each => (
                                                    <tr key={each.field}>
                                                        <td className='rr-field'>{each.label}</td>
                                                        <td className='rr-old'><ChangeValue value={each.old} /></td>
                                                        <td className='rr-new'><ChangeValue value={each.new} /></td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                        : null
                                    }
                                </div>
                                : null
                            }
                        </div>
                        : null
                    }
                    {reReview.status === 'failed' ? <div className='rr-error'>Re-review failed{reReview.error ? `: ${reReview.error}` : ''}</div> : null}
                    {reReview.status === 'superseded' ? <div className='rr-muted'>Not re-reviewed - the job was actioned before it was due</div> : null}
                </div>
                : null
            }
            {salaryNote ? <div className='rr-salary'>Salary is hidden - it will go live disclosed when this job is published</div> : null}
        </div>
    );
}

ReReviewSummary.propTypes = {
    item: PropTypes.object.isRequired,
};

export default ReReviewSummary;
